import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { chatAPI, conversationsAPI } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocketToken';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Send, Plus, MessageSquare, Bot, User, Loader2, ArrowUpRight, AlertTriangle, Zap, Shield, Square, Trash2 } from 'lucide-react';
import { MessageAttachments } from '@/components/MessageAttachments';
import ConversationListItem, { getConversationDisplay } from '@/components/ConversationListItem';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import { toast } from 'sonner';
import { fechaYHora, soloHora, soloFecha } from '../lib/fechas';

// Format timestamp like WhatsApp: "HH:MM"
function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return soloHora(d, '');
}

// Format date for separator: "Hoy", "Ayer", or "DD/MM/YYYY"
function formatDateSeparator(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (today - msgDay) / (1000 * 60 * 60 * 24);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Ayer';
  return soloFecha(d, '');
}

// Check if two timestamps are on different days
function isDifferentDay(ts1, ts2) {
  if (!ts1 || !ts2) return true;
  const d1 = new Date(ts1);
  const d2 = new Date(ts2);
  return d1.toDateString() !== d2.toDateString();
}

export default function ChatPage() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const canDeleteConv = user?.role === 'admin' || user?.role === 'supervisor';
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingAgentRunId, setPendingAgentRunId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, conv: null });
  const messagesEndRef = useRef(null);
  const capsuleId = 'default';

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => { if (activeConvId) loadConversation(activeConvId); }, [activeConvId]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadConversations();
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(loadConversations, 30000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, []);

  const handleWsMessage = useCallback((data) => {
    if (data.conversation_id !== activeConvId) return;
    if (data.type === 'new_message') {
      setMessages(prev => {
        if (prev.find(m => m.message_id === data.message.message_id)) return prev;
        return [...prev, data.message];
      });
    } else if (data.type === 'agent_run_started') {
      setPendingAgentRunId(data.run_id || null);
    } else if (data.type === 'agent_run_finished') {
      setPendingAgentRunId((cur) => (cur && data.run_id === cur ? null : cur));
    }
  }, [activeConvId]);

  useWebSocket(activeConvId ? `/ws/chat/${activeConvId}` : null, {
    enabled: !!activeConvId,
    onMessage: handleWsMessage,
  });

  const loadConversations = async () => {
    try {
      const res = await chatAPI.listConversations();
      const list = (res.data || []).filter((c) => c.status !== 'merged');
      setConversations(list);
    } catch (err) { console.error(err); }
  };

  const loadConversation = async (convId) => {
    try {
      const res = await chatAPI.getConversation(convId);
      setActiveConv(res.data);
      setMessages(res.data?.messages || []);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 410 && detail?.merged_into) {
        toast.info('Conversacion fusionada; abriendo destino');
        setActiveConvId(detail.merged_into);
        return;
      }
      console.error(err);
    }
  };

  const handleDeleteConversation = async (conv) => {
    if (!conv?.conversation_id || !tenantId) return;
    const capId = conv.capsule_id || capsuleId;
    try {
      await conversationsAPI.delete(tenantId, capId, conv.conversation_id);
      toast.success('Conversacion eliminada');
      if (activeConvId === conv.conversation_id) {
        setActiveConvId(null);
        setActiveConv(null);
        setMessages([]);
      }
      await loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar la conversacion');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    setPendingAgentRunId(null);

    const tempMsg = {
      message_id: `temp-${Date.now()}`,
      sender_type: 'user',
      sender_name: user?.full_name,
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const res = await chatAPI.send({
        conversation_id: activeConvId || undefined,
        capsule_id: capsuleId,
        message: text,
      });
      const {
        conversation_id,
        agent_response,
        agent_id,
        model_used,
        user_message_id,
        user_attachments,
        user_cancelled,
      } = res.data;

      if (!activeConvId) {
        setActiveConvId(conversation_id);
        loadConversations();
      }

      setMessages((prev) =>
        prev.map((m) => {
          if (String(m.message_id || '').startsWith('temp-')) {
            return {
              ...m,
              message_id: user_message_id || m.message_id,
              attachments: user_attachments != null ? user_attachments : m.attachments || [],
            };
          }
          return m;
        })
      );

      if (agent_response) {
        setMessages(prev => [
          ...prev,
          {
            message_id: res.data.message_id,
            sender_type: agent_id === 'system' ? 'system' : 'agent',
            sender_name: agent_id,
            content: agent_response,
            timestamp: new Date().toISOString(),
            metadata: { model_used, user_cancelled },
          },
        ]);
      }
      setPendingAgentRunId(null);
    } catch (err) {
      console.error('Send error:', err);
      setMessages(prev => [
        ...prev,
        { message_id: 'error', sender_type: 'system', content: 'Error enviando mensaje', timestamp: new Date().toISOString() },
      ]);
    } finally {
      setSending(false);
      setPendingAgentRunId(null);
    }
  };

  const handleStopAgent = async () => {
    if (!pendingAgentRunId) return;
    try {
      await chatAPI.cancelAgentRun(pendingAgentRunId);
    } catch (e) {
      console.error('Cancel agent run:', e);
    }
  };

  const startNewConversation = () => {
    setActiveConvId(null);
    setActiveConv(null);
    setMessages([]);
  };

  const getLastMessage = (conv) => {
    const msgs = conv.messages || [];
    if (msgs.length === 0) return 'Sin mensajes';
    return msgs[msgs.length - 1]?.content?.slice(0, 50) + '...' || '';
  };

  const getConvOwner = (conv) => {
    const msgs = conv.messages || [];
    const firstUserMsg = msgs.find(m => m.sender_type === 'user');
    return firstUserMsg?.sender_name || conv.channel_user_id || '';
  };

  const intentLabel = activeConv?.intent || activeConv?.classification?.intent;
  const agentLabel = activeConv?.current_agent?.agent_id;

  return (
    <div className="flex h-full" data-testid="chat-page">
      {/* Conversation list */}
      <div className="w-72 border-r border-brand-border bg-white flex flex-col">
        <div className="h-14 flex items-center justify-between px-4 border-b border-brand-border">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">Conversaciones</span>
          <Button variant="ghost" size="icon" data-testid="new-conversation-button" onClick={startNewConversation} className="h-8 w-8 hover:bg-brand-surface">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto">
          {conversations.length === 0 ? (
            <p className="p-4 text-xs text-zinc-400">Inicia una nueva conversacion</p>
          ) : (
            conversations.map(conv => (
              <ConversationListItem
                key={conv.conversation_id}
                conv={conv}
                selected={activeConvId === conv.conversation_id}
                onClick={() => setActiveConvId(conv.conversation_id)}
                formatTime={formatTime}
                className=""
                trailing={
                  <div className="flex items-center gap-1">
                    {conv.status === 'escalated' ? <ArrowUpRight className="w-3 h-3 text-brand-danger" title="Necesita que intervenga una persona" /> : null}
                    {canDeleteConv && (
                      <button
                        type="button"
                        className="text-zinc-300 hover:text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete({ open: true, conv });
                        }}
                        title="Eliminar conversacion"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                }
              />
            ))
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Chat header with classification info */}
        {activeConv && (
          <div className="h-12 flex items-center justify-between px-6 border-b border-brand-border" data-testid="chat-header">
            <div className="flex items-center gap-3 min-w-0">
              <Zap className="w-4 h-4 text-brand-primary shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#09090B] truncate">
                  {getConversationDisplay(activeConv).primary}
                </p>
                <p className="text-[10px] text-zinc-500 truncate">
                  {getConversationDisplay(activeConv).secondary}
                  {agentLabel && (
                    <span className="font-mono text-brand-primary ml-2">{agentLabel}</span>
                  )}
                </p>
              </div>
              {intentLabel && (
                <Badge variant="secondary" className="text-xs rounded-sm">{intentLabel}</Badge>
              )}
              {activeConv.priority && (
                <Badge
                  className={`text-xs rounded-sm ${
                    activeConv.priority === 'critical' ? 'bg-red-50 text-brand-danger' :
                    activeConv.priority === 'high' ? 'bg-orange-50 text-orange-700' :
                    'bg-zinc-100 text-zinc-600'
                  }`}
                >
                  {activeConv.priority}
                </Badge>
              )}
              {activeConv.hitl?.is_human_active && (
                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 rounded-sm">HITL Activo</Badge>
              )}
            </div>
            {activeConv.created_at && (
              <span className="text-[10px] text-zinc-400">Creado: {fechaYHora(activeConv.created_at)}</span>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-auto p-6 space-y-1" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-zinc-400">
              <Bot className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm">Envia un mensaje para comenzar</p>
              <p className="text-xs mt-1">El Coordinador clasificara tu consulta y asignara el agente adecuado</p>
            </div>
          )}
          {messages.map((msg, idx) => {
            const prevMsg = idx > 0 ? messages[idx - 1] : null;
            const showDateSep = isDifferentDay(prevMsg?.timestamp, msg.timestamp);
            const isUser = msg.sender_type === 'user';
            const isHuman = msg.sender_type === 'human';
            const isSystem = msg.sender_type === 'system';
            const isAgent = msg.sender_type === 'agent';

            return (
              <div key={msg.message_id}>
                {/* Date separator */}
                {showDateSep && msg.timestamp && (
                  <div className="flex items-center justify-center my-3" data-testid={`date-sep-${idx}`}>
                    <span className="text-[10px] text-zinc-400 bg-zinc-100 px-3 py-1 rounded-full">
                      {formatDateSeparator(msg.timestamp)}
                    </span>
                  </div>
                )}
                <div
                  className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-1`}
                  data-testid={`chat-msg-${msg.message_id}`}
                >
                  <div
                    className={`max-w-[70%] px-4 py-2 rounded-sm ${
                      isUser
                        ? 'bg-brand-primary text-white'
                        : isHuman
                        ? 'bg-emerald-50 text-[#09090B] border border-emerald-200'
                        : isSystem
                        ? 'bg-brand-warning/10 text-zinc-700 border border-brand-warning/30'
                        : 'bg-brand-surface text-[#09090B] border border-brand-border'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      {isUser && <User className="w-3 h-3" />}
                      {isAgent && <Bot className="w-3 h-3" />}
                      {isHuman && <Shield className="w-3 h-3 text-emerald-600" />}
                      {isSystem && <AlertTriangle className="w-3 h-3" />}
                      <span className="text-[11px] font-medium opacity-80">
                        {msg.sender_name || (isUser ? 'Usuario' : isHuman ? 'Supervisor' : isAgent ? 'Agente' : 'Sistema')}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    {activeConvId && (
                      <MessageAttachments
                        conversationId={activeConvId}
                        message={msg}
                        isUserBubble={isUser}
                      />
                    )}
                    <div className="flex items-center justify-end gap-2 mt-1">
                      {msg.metadata?.model_used && (
                        <span className="text-[10px] opacity-40 font-mono">{msg.metadata.model_used}</span>
                      )}
                      {msg.timestamp && (
                        <span className={`text-[10px] ${isUser ? 'text-white/60' : 'text-zinc-400'}`}>
                          {formatTime(msg.timestamp)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-brand-surface border border-brand-border px-4 py-3 rounded-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                <span className="text-xs text-zinc-400">Procesando...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-brand-border p-4">
          <form onSubmit={handleSend} className="flex gap-2" data-testid="chat-input-form">
            <Input
              data-testid="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Escribe tu mensaje..."
              className="flex-1 h-10 border-brand-border focus:ring-2 focus:ring-brand-primary text-sm rounded-sm"
              disabled={sending}
            />
            {sending && pendingAgentRunId && (
              <Button
                type="button"
                variant="outline"
                data-testid="chat-stop-agent-button"
                onClick={handleStopAgent}
                className="h-10 px-3 border-brand-danger text-brand-danger hover:bg-red-50 shrink-0"
              >
                <Square className="w-3 h-3 mr-1.5 fill-current" />
                Detener
              </Button>
            )}
            <Button
              type="submit"
              data-testid="chat-send-button"
              disabled={sending || !input.trim()}
              className="h-10 px-4 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm transition-colors duration-150"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
      <ConfirmActionDialog
        open={confirmDelete.open}
        onOpenChange={(open) => setConfirmDelete((s) => ({ ...s, open }))}
        title="Eliminar conversacion"
        description={
          confirmDelete.conv
            ? `Se eliminara permanentemente la conversacion ${confirmDelete.conv.conversation_id?.slice(0, 8)}.`
            : ''
        }
        confirmText="Eliminar"
        onConfirm={async () => {
          if (confirmDelete.conv) await handleDeleteConversation(confirmDelete.conv);
        }}
      />
    </div>
  );
}
